import * as React from 'react';

import type { ApolloQueryResult, OperationVariables } from 'apollo-client';
import type { DocumentNode } from 'graphql';

import { createApolloClient } from 'bundles/page/lib/network/Apollo';

import useDeepMemo from './useDeepMemo';

type QueryHookOptions<TVariables> = {
  skip?: boolean;
  variables: TVariables;
};

/**
 * An alternative Apollo hook.
 *
 * WARNING: This hook is only for experimental purpose in `internal-enterprise`
 *          and should NOT be used by other bundles.
 *          If you have the needs, please consider using `useQuery` from Apollo 3.
 *
 * Usage:
 *     const result = useGqlQuery(gql`query ...`, { variables: {}, skip: false });
 *     console.log(result?.data.resource.id);
 * Unfortunately this hook does not support cache, so it won't share the data across pages.
 *
 * This hook creates a variable that "automatically updates" when the dependencies (query and options) change.
 * As in the above example, `result` will "automatically" updates when query or options change.
 * Under the hood, `useGqlQuery` will run on every render.
 * If query or options changed, it will execute the query then return the response.
 * Otherwise, it will return cached previous response.
 *
 * This feature makes it easily being changed:
 *
 *     const result1 = useGqlQuery(query1, input);
 *     const result2 = useGqlQuery(query2, { variables: { id: result2.id } });
 *     const result3 = useGqlQuery(query3, { variables: { id: result3.id } });
 *     console.log(result3.id);
 *
 * Changes on input will propagate through the chain and update each result.
 *
 * @param query GraphQL query, created with `gql`
 * @param options `{ variables, skip }`
 * @returns
 */
const useGqlQuery = <TData, TVariables extends OperationVariables | undefined>(
  query: DocumentNode,
  options: QueryHookOptions<TVariables>
) => {
  const [result, setResult] = React.useState<ApolloQueryResult<TData>>();
  const { variables, skip } = options;

  // use `useDeepMemo` to send Gql query only if `{ query, options }` is deeply changed.
  // cannot use `useEffect` since the `{ query, options }` is a complicated object,
  // and both `query` and `options` are usually passed in an ad-hoc manner,
  //   (e.g.   useGqlQuery(gql`query {...}`, { variables: {}, skip: false } ))
  // which means every `useGqlQuery` call gets different `query` and `optionis` value.
  // This will cause the query to be sent on every render.
  // Therefore, we need to deeply compare the value of `{ query, options }`.
  const memo = { query, options };

  useDeepMemo(() => {
    if (skip) {
      setResult(undefined);

      return undefined;
    } else {
      // creating a client ad-hoc is not recommended but there's no way to get the client from the legacy context.
      const client = createApolloClient();

      // return value (of type `Promise<ApolloQueryResult<TData>> | undefined`) is not actually necessary,
      // but only to provide something to be stored in the memoized store (the `ref` in the `useDeepMemo`).
      // Might be useful for future.
      // Here the `setResult` actually matters.
      return (
        client
          .query<TData>({ query, variables })
          // unwrap the ApolloQueryResult<TData> from the promise.
          .then(setResult)
      );
    }
  }, memo);

  return result;
};

export default useGqlQuery;
